/**
 * Erkennt Ausnahmen, die die MESSUNG SELBST erzeugt hat — nicht das Produkt.
 *
 * ## Warum es diese Datei gibt
 *
 * Am 23.08.2026 meldete Produktion vier `DOMException: NotReadableError: The I/O
 * read operation failed.` Der Vorgang #1086 hat sie als „fehlgeschlagenen
 * Foto-Upload" gedeutet und daraus eine Massnahme abgeleitet („Foto-Upload
 * haerten"). Beides war falsch:
 *
 *   - Die vier Ereignisse lagen **57 ms** auseinander (22:11:30.049–.106) — das
 *     ist ein Warteschlangen-Flush, kein Nutzerklick.
 *   - Ihre `$current_url` war die Arbeitsbereich-Uebersicht, **nicht** der
 *     Assistent.
 *   - Ihr Stack lief durch `retriableRequest` → `_send_request` → `_gzipCompress`
 *     → `CompressionStream` — also durch den Sendeweg von posthog-js. Geraet:
 *     Mobile Safari 16.4, und `CompressionStream` ist in genau dieser Version
 *     eingefuehrt worden.
 *
 * Eine Messung, die ihre eigenen Netz- und Kompressionsfehler als Produktfehler
 * meldet, schickt jede Auswertung in die falsche Richtung. Genau das ist
 * passiert, und ohne diesen Filter passiert es wieder.
 *
 * ## Warum die Bedingung so eng ist
 *
 * Nahe liegend waere „Ereignisse ohne Stack-Rahmen verwerfen". Das waere falsch:
 * die opaken `"Script error."`-Ereignisse (#1052) sind ebenfalls rahmenlos, und
 * die wollen wir **behalten** — sie sind das einzige Signal fuer Fehler aus
 * Fremdskripten.
 *
 * Ebenso falsch waere ein Filter ueber den Dateinamen: posthog-js wird in unser
 * eigenes Bundle gepackt, seine Rahmen tragen also `/_next/static/chunks/…` wie
 * jeder andere Code auch.
 *
 * Der Anker sind deshalb die **Funktionsnamen des Sendewegs**. Sie ueberleben
 * die Minifizierung, weil es Objekt-Eigenschaften und keine lokalen Variablen
 * sind — im Prod-Stack vom 23.08. stehen `retriableRequest` und `_send_request`
 * im Klartext. Gegengeprueft am 30.08.2026: die drei Namen kommen in
 * `posthog-js@1.369.2` vor und in unserem eigenen Quelltext **null mal**
 * (`grep` ueber `apps/` und `packages/` ohne `node_modules`).
 *
 * Ein echter Produktfehler kann diese Bedingung damit praktisch nicht erfuellen.
 * Das ist Absicht: einen echten Fehler zu verschlucken waere schlimmer als das
 * Rauschen zu behalten.
 */

/**
 * Methodennamen aus dem Sendeweg von posthog-js. Ausschliesslich dort vergeben.
 *
 * 🔴 Beim Anheben der posthog-js-Version gegenpruefen: werden diese Namen
 * umbenannt, greift der Filter still nicht mehr, und das Rauschen kommt zurueck.
 * Es faellt dann in der Fehleruebersicht auf — der Filter kann nicht in die
 * gefaehrliche Richtung ausfallen, nur in die harmlose.
 */
const SENDEWEG_RAHMEN = [
  '_send_request',
  '_send_retriable_request',
  'retriableRequest',
];

interface Rahmen {
  function?: string | null;
}

interface AusnahmeEintrag {
  stacktrace?: { frames?: Rahmen[] | null } | null;
}

interface MoeglichesEreignis {
  event?: string;
  properties?: Record<string, unknown> | null;
}

/**
 * @returns true, wenn das Ereignis eine Ausnahme aus dem Sendeweg von posthog-js
 *   ist — also Rauschen der Messung selbst und kein Produktfehler.
 *
 * Gibt fuer alles andere `false` zurueck, insbesondere auch fuer Ereignisse ohne
 * Stack (siehe Kopfkommentar) und fuer alles, was kein `$exception` ist.
 */
export function istEigenesMessrauschen(
  ereignis: MoeglichesEreignis | null | undefined,
): boolean {
  if (ereignis?.event !== '$exception') return false;

  const liste = ereignis.properties?.$exception_list;

  if (!Array.isArray(liste)) return false;

  return (liste as AusnahmeEintrag[]).some((eintrag) => {
    const rahmen = eintrag?.stacktrace?.frames;

    if (!Array.isArray(rahmen)) return false;

    return rahmen.some((r) => {
      const name = r?.function;

      return typeof name === 'string' && SENDEWEG_RAHMEN.includes(name);
    });
  });
}
